import React from 'react';
import { Box, Package, Boxes } from 'lucide-react';
import './RoomTypeSelector.css';

interface RoomOption {
  id: string;
  title: string;
  subtitle: string;
  icon: React.ReactNode;
}

interface RoomTypeSelectorProps {
  onSelect: (value: string, shouldAutoAdvance?: boolean) => void;
}

// Room icons using Lucide React
const SmallRoomIcon = () => (
  <div className="room-icon">
    <Box size={36} strokeWidth={1.5} className="room-cube-icon" />
  </div>
);

const MediumRoomIcon = () => (
  <div className="room-icon">
    <Package size={40} strokeWidth={1.5} className="room-cube-icon" />
  </div>
);

const LargeRoomIcon = () => (
  <div className="room-icon">
    <Package size={44} strokeWidth={1.5} className="room-cube-icon" />
  </div>
);

const AuditoriumIcon = () => (
  <div className="room-icon">
    <Boxes size={42} strokeWidth={1.5} className="room-cube-icon" />
  </div>
);

const roomOptions: RoomOption[] = [
  {
    id: 'small',
    title: 'Small room',
    subtitle: '1-10',
    icon: <SmallRoomIcon />
  },
  {
    id: 'medium',
    title: 'Medium room',
    subtitle: '11-20',
    icon: <MediumRoomIcon />
  },
  {
    id: 'large',
    title: 'Large room',
    subtitle: '21-50',
    icon: <LargeRoomIcon />
  },
  {
    id: 'auditorium',
    title: 'Auditorium',
    subtitle: '50+',
    icon: <AuditoriumIcon />
  }
];

// Map room types to quiz values
const mapRoomTypeToQuizValue = (roomType: string): string => {
  switch (roomType) {
    case 'small':
      return 'Small Room / Huddle Space';
    case 'medium':
      return 'Medium Room';
    case 'large':
      return 'Large Room';
    case 'auditorium':
      return 'Large Room'; // Auditorium maps to Large Room in the quiz
    default:
      return 'Medium Room';
  }
};

export const RoomTypeSelector: React.FC<RoomTypeSelectorProps> = ({ onSelect }) => {
  const [selectedRoom, setSelectedRoom] = React.useState<string | null>(null);

  const handleRoomSelect = (roomType: string) => {
    setSelectedRoom(roomType);
    const quizValue = mapRoomTypeToQuizValue(roomType);
    onSelect(quizValue);
  };

  return (
    <div className="room-type-selector">
      <div className="room-options-grid">
        {roomOptions.map((option) => (
          <button
            key={option.id}
            className={`room-option-card ${selectedRoom === option.id ? 'selected' : ''}`}
            onClick={() => handleRoomSelect(option.id)}
            disabled={selectedRoom !== null}
          >
            {option.icon}
            <h3 className="room-option-title">{option.title}</h3>
            <div className="room-option-subtitle">
              <i className="person-icon"></i>
              <span>{option.subtitle}</span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};
